type Tap = {
  tile: HTMLElement;
  x: number;
  y: number;
  time: number;
};

/** Validate touch taps without consuming the browser's scrolling/pinch gestures. */
export class LibraryTap {
  private active: (Tap & { id: number }) | undefined;
  private completed: Tap | undefined;
  private previous: Tap | undefined;
  fromTouch = false;

  constructor(grid: HTMLElement) {
    grid.addEventListener("pointerdown", (e) => {
      this.fromTouch = e.pointerType === "touch";
      this.completed = undefined;
      const tile = (e.target as Element).closest<HTMLElement>(
        ".clipart-tile[data-file]",
      );
      if (!this.fromTouch || !e.isPrimary || !tile) {
        this.reset();
        return;
      }
      this.active = {
        tile,
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        time: e.timeStamp,
      };
    });
    grid.addEventListener("pointermove", (e) => {
      if (this.active?.id === e.pointerId && !this.near(this.active, e, 12))
        this.reset();
    });
    grid.addEventListener("pointerup", (e) => {
      const tap = this.active;
      if (!tap || tap.id !== e.pointerId) return;
      this.active = undefined;
      const bounds = tap.tile.getBoundingClientRect();
      if (
        e.timeStamp - tap.time > 500 ||
        !this.near(tap, e, 12) ||
        e.clientX < bounds.left ||
        e.clientX > bounds.right ||
        e.clientY < bounds.top ||
        e.clientY > bounds.bottom
      ) {
        this.reset();
        return;
      }
      this.completed = { ...tap, time: e.timeStamp };
    });
    grid.addEventListener("pointercancel", () => this.reset());
    grid.addEventListener("scroll", () => this.reset(), { passive: true });
  }

  reset() {
    this.active = this.completed = this.previous = undefined;
  }

  click(tile: HTMLElement, event: MouseEvent): "single" | "double" | "ignore" {
    // Keyboard/assistive activation selects normally; mouse uses native dblclick.
    if (!this.fromTouch || event.detail === 0) {
      this.reset();
      return "single";
    }
    const tap = this.completed;
    this.completed = undefined;
    if (!tap || tap.tile !== tile) return "ignore";
    const previous = this.previous;
    this.previous = tap;
    if (
      previous?.tile === tile &&
      tap.time - previous.time <= 450 &&
      Math.hypot(tap.x - previous.x, tap.y - previous.y) <= 24
    ) {
      this.reset();
      return "double";
    }
    return "single";
  }

  private near(tap: Tap, e: PointerEvent, distance: number) {
    return Math.hypot(tap.x - e.clientX, tap.y - e.clientY) <= distance;
  }
}
